import React, { useState, SetStateAction } from 'react';
import CVUpload from '../components/cv/CVUpload';
import type { CVAnalysis } from '../types/analysis';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { jobsApi } from '../services/api/jobs';
const CVAnalysisPage: React.FC = () => {
  const [analysis, setAnalysis] = useState<CVAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalysis = async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      const result = await jobsApi.analyzeCV(file);
      setAnalysis(result);
    } catch (err: any) {
      console.error('CV analysis failed:', err);
      setError(err.message || 'Failed to analyze CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">CV Analysis</h1>
      
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-8">
          {error}
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {!analysis ? (
          <CVUpload onUpload={handleAnalysis} loading={loading} />
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">Match Score</h2>
                    <div className="text-4xl font-bold">
                      {Math.round(analysis.analysis.score * 100)}%
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="w-full bg-blue-800/30 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${analysis.analysis.score * 100}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="bg-white h-3 rounded-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Matching Skills */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Matching Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.analysis.details.skills_analysis.matched_skills.map((skill: string, index: number) => (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                          className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100 flex items-center"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1 text-blue-500" />
                          {skill}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Missing Skills */}
                  {analysis.analysis.details.skills_analysis.missing_skills.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4">Skills to Acquire</h3>
                      <div className="flex flex-wrap gap-2">
                        {analysis.analysis.details.skills_analysis.missing_skills.map((skill: string, index: number) => (
                          <span
                            key={index}
                            className="px-4 py-2 bg-gray-50 text-gray-600 rounded-full text-sm border border-gray-200"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysis.analysis.details.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
                      <div className="space-y-2">
                        {analysis.analysis.details.recommendations.map((recommendation: string, index: number) => (
                          <div
                            key={index}
                            className="bg-yellow-50 border-l-4 border-yellow-400 p-4"
                          >
                            <p className="text-yellow-700">{recommendation}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => setAnalysis(null)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Analyze Another CV
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default CVAnalysisPage; 